import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Compass, MapPin, CalendarDays, Radio, Lightbulb, Trophy,
  Store, Star, Download, FileSpreadsheet, FileDown, Filter, PieChart, ClipboardCheck,
} from 'lucide-react';
import { useFilters } from '@/context/FilterContext';
import { usePost } from '@/lib/useData';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { GridSkeleton, CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { HorizontalBars } from '@/components/charts/HorizontalBars';
import { ComparisonBars } from '@/components/charts/ComparisonBars';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { SpotPareto } from '@/components/geo/SpotPareto';
import { BookingPlan } from '@/components/geo/BookingPlan';
import { downloadPost } from '@/lib/download';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';

const GEO_METRICS = {
  bs_reel: { label: 'BS validés', fmt: (v) => fmtInt(v) },
  taux_transfo: { label: 'Taux de transformation', fmt: (v) => fmtPct(v) },
  montant: { label: 'Montant collecté', fmt: (v) => fmtEur(v, 0) },
  productivite: { label: 'Productivité', fmt: (v) => `${fmtNum(v, 2)} BS/h` },
  don_moyen: { label: 'Don moyen', fmt: (v) => fmtEur(v, 2) },
  age_moyen: { label: 'Âge moyen', fmt: (v) => `${fmtNum(v, 1)} ans` },
};

const InsightCard = ({ icon: Icon, label, value, detail, testid }) => (
  <div className="rounded-2xl border bg-card p-5 card-shadow" data-testid={testid}>
    <div className="flex items-center gap-2 text-muted-foreground">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl accent-bg-100">
        <Icon className="h-4.5 w-4.5 accent-text" />
      </span>
      <span className="text-xs uppercase tracking-wide">{label}</span>
    </div>
    <p className="mt-3 break-words font-display text-xl font-semibold text-foreground" title={value}>{value}</p>
    {detail && <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>}
  </div>
);

const GeoTable = ({ rows, labelHead, testid }) => (
  <div className="overflow-hidden rounded-xl border">
    <Table data-testid={testid}>
      <TableHeader>
        <TableRow className="bg-muted/60 hover:bg-muted/60">
          <TableHead className="font-semibold text-foreground">{labelHead}</TableHead>
          <TableHead className="text-right">BS signés</TableHead>
          <TableHead className="text-right">BS validés</TableHead>
          <TableHead className="text-right">Transfo</TableHead>
          <TableHead className="text-right">Don moyen</TableHead>
          <TableHead className="text-right">Montant</TableHead>
          <TableHead className="text-right">Âge moyen</TableHead>
          <TableHead className="text-right">Prod.</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.key} data-testid={`${testid}-row`}>
            <TableCell className="font-medium">{r.key}</TableCell>
            <TableCell className="text-right tabular">{fmtInt(r.bs_rue)}</TableCell>
            <TableCell className="text-right tabular font-semibold">{fmtInt(r.bs_reel)}</TableCell>
            <TableCell className="text-right tabular">{fmtPct(r.taux_transfo)}</TableCell>
            <TableCell className="text-right tabular">{fmtEur(r.don_moyen, 2)}</TableCell>
            <TableCell className="text-right tabular">{fmtEur(r.montant, 0)}</TableCell>
            <TableCell className="text-right tabular">{fmtNum(r.age_moyen, 1)} ans</TableCell>
            <TableCell className="text-right tabular">{fmtNum(r.productivite, 2)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

const DAY_ABBR = { Lundi: 'Lun', Mardi: 'Mar', Mercredi: 'Mer', Jeudi: 'Jeu', Vendredi: 'Ven', Samedi: 'Sam', Dimanche: 'Dim' };

function SpotsPanel({ body }) {
  // Fetch all spots once (min_days=1); threshold/region/min-days refined client-side.
  const { data, isLoading, isError, error, refetch } = usePost('/admin/geo/best-spots', { ...body, threshold: 6.5, min_days: 1 });
  const [threshold, setThreshold] = useState(6.5);
  const [region, setRegion] = useState('all');
  const [minDays, setMinDays] = useState(1);
  const [onlyInteresting, setOnlyInteresting] = useState(false);
  const [exporting, setExporting] = useState(null);

  const allSpots = data?.spots || [];
  const regions = data?.regions || [];

  const spots = useMemo(() => {
    return allSpots
      .filter((s) => (region === 'all' ? true : s.region === region))
      .filter((s) => s.days >= minDays)
      .map((s) => ({ ...s, interesting: s.avg_bs_rue >= threshold }))
      .filter((s) => (onlyInteresting ? s.interesting : true));
  }, [allSpots, region, minDays, threshold, onlyInteresting]);

  const interestingCount = spots.filter((s) => s.interesting).length;

  const runExport = async (kind) => {
    setExporting(kind);
    try {
      const ext = kind === 'excel' ? 'xlsx' : 'pdf';
      await downloadPost(`/admin/geo/best-spots/${kind}`,
        { ...body, threshold, region, min_days: minDays },
        `SHARITI_meilleurs_spots.${ext}`);
      toast.success('Export généré');
    } catch {
      toast.error('Export impossible');
    } finally {
      setExporting(null);
    }
  };

  if (isError) {
    if (error?.response?.status === 403) {
      return <EmptyState title="Accès restreint" body="Cette analyse est réservée aux administrateurs SHARITI." />;
    }
    return <ErrorState title="Impossible de charger les spots" body="Le calcul a échoué ou expiré. Réessayez dans un instant." onRetry={refetch} />;
  }
  if (isLoading) return <><GridSkeleton count={3} /><CardSkeleton /></>;

  return (
    <div className="space-y-6" data-testid="geo-spots-panel">
      {/* Intro */}
      <div className="flex items-start gap-3 rounded-2xl border p-4 accent-bg-soft" data-testid="spots-intro">
        <Store className="mt-0.5 h-5 w-5 accent-text shrink-0" />
        <p className="text-sm text-foreground">
          Classement des <span className="font-semibold">lieux de recrutement</span> par moyenne de bulletins <span className="font-semibold">bruts par jour</span>.
          Un spot est jugé <span className="font-semibold">intéressant</span> au-delà du seuil défini — idéal pour orienter l'équipe booking vers les emplacements qui performent.
        </p>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 gap-4 rounded-2xl border bg-card p-4 card-shadow md:grid-cols-4" data-testid="spots-controls">
        <div className="md:col-span-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Seuil « spot intéressant »</span>
            <span className="tabular font-display text-sm font-semibold accent-text" data-testid="spots-threshold-value">≥ {fmtNum(threshold, 1)} BS brut/j</span>
          </div>
          <Slider value={[threshold]} min={2} max={20} step={0.5} onValueChange={(v) => setThreshold(v[0])}
            className="mt-3" data-testid="spots-threshold-slider" />
        </div>
        <div>
          <span className="text-xs font-medium text-muted-foreground">Région</span>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="mt-1.5 h-9 rounded-xl bg-card" data-testid="spots-region-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les régions</SelectItem>
              {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <span className="text-xs font-medium text-muted-foreground">Fiabilité (jours min.)</span>
          <Select value={String(minDays)} onValueChange={(v) => setMinDays(Number(v))}>
            <SelectTrigger className="mt-1.5 h-9 rounded-xl bg-card" data-testid="spots-mindays-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Tous les spots</SelectItem>
              <SelectItem value="2">≥ 2 jours</SelectItem>
              <SelectItem value="3">≥ 3 jours</SelectItem>
              <SelectItem value="5">≥ 5 jours</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary + actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 text-sm">
          <Badge variant="outline" className="rounded-full border-transparent accent-bg-soft accent-text font-medium" data-testid="spots-interesting-count">
            <Star className="mr-1 h-3.5 w-3.5" fill="currentColor" /> {interestingCount} spot(s) intéressant(s)
          </Badge>
          <span className="text-muted-foreground">sur {spots.length} affiché(s)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="mr-1 flex items-center gap-2">
            <Switch checked={onlyInteresting} onCheckedChange={setOnlyInteresting} data-testid="spots-only-interesting" />
            <span className="text-xs text-muted-foreground">Intéressants seulement</span>
          </div>
          <Button variant="outline" size="sm" className="gap-1.5 rounded-xl" onClick={() => runExport('excel')}
            disabled={exporting === 'excel'} data-testid="spots-export-excel">
            <FileSpreadsheet className="h-4 w-4" /><span className="hidden sm:inline">{exporting === 'excel' ? '…' : 'Excel'}</span>
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5 rounded-xl" onClick={() => runExport('pdf')}
            disabled={exporting === 'pdf'} data-testid="spots-export-pdf">
            <FileDown className="h-4 w-4" /><span className="hidden sm:inline">{exporting === 'pdf' ? '…' : 'PDF'}</span>
          </Button>
        </div>
      </div>

      {/* Table */}
      <SectionCard title="Classement des spots" subtitle="Moyenne de bulletins bruts par journée d'exploitation" bodyClassName="pt-0" testid="spots-table-card">
        {spots.length === 0 ? (
          <EmptyState title="Aucun spot" body="Aucun spot ne correspond aux filtres actuels." />
        ) : (
          <div className="overflow-hidden rounded-xl border">
            <Table data-testid="spots-table">
              <TableHeader>
                <TableRow className="bg-muted/60 hover:bg-muted/60">
                  <TableHead className="w-10 text-center">#</TableHead>
                  <TableHead className="font-semibold text-foreground">Spot / Lieu</TableHead>
                  <TableHead>Région</TableHead>
                  <TableHead className="text-right">Moy. BS brut/j</TableHead>
                  <TableHead className="text-right">Jours</TableHead>
                  <TableHead>Meilleurs jours</TableHead>
                  <TableHead className="text-right">Transfo</TableHead>
                  <TableHead className="text-right">Don moyen</TableHead>
                  <TableHead className="text-right">Âge moy.</TableHead>
                  <TableHead>Asso reco.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {spots.map((s, i) => (
                  <TableRow key={s.lieu} className={s.interesting ? 'accent-bg-soft' : ''} data-testid="spots-table-row">
                    <TableCell className="text-center text-xs text-muted-foreground tabular">{i + 1}</TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-1.5">
                        {s.interesting && <Star className="h-3.5 w-3.5 shrink-0 accent-text" fill="currentColor" />}
                        <span className="break-words" title={s.lieu}>{s.lieu}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.region}</TableCell>
                    <TableCell className="text-right">
                      <span className={`tabular font-display font-semibold ${s.interesting ? 'accent-text' : ''}`}>{fmtNum(s.avg_bs_rue, 1)}</span>
                    </TableCell>
                    <TableCell className="text-right tabular text-muted-foreground">{fmtInt(s.days)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(s.best_days || []).map((d) => (
                          <span key={d} className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium">{DAY_ABBR[d] || d}</span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular">{fmtPct(s.taux_transfo)}</TableCell>
                    <TableCell className="text-right tabular">{fmtEur(s.don_moyen, 2)}</TableCell>
                    <TableCell className="text-right tabular">{s.age_moyen ? `${fmtNum(s.age_moyen, 1)} ans` : '—'}</TableCell>
                    <TableCell className="text-xs">{s.reco_association_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

export default function Territoires() {
  const { body, options } = useFilters();
  const [view, setView] = useState('executive');
  const [metric, setMetric] = useState('bs_reel');
  const { data, isLoading, isError, error, refetch } = usePost('/admin/geo/analysis', body);
  const isForbidden = error?.response?.status === 403;

  const territories = data?.territories || [];
  const days = data?.days || [];
  const channels = data?.channels || [];
  const ins = data?.insights || {};

  const territoryBars = useMemo(
    () => territories.slice(0, 8).map((t) => ({ key: t.key, value: t[metric] })),
    [territories, metric]
  );
  const dayBars = useMemo(
    () => days.map((d) => ({ label: d.key, value: d[metric] })),
    [days, metric]
  );

  // Heatmap intensity scale (max bs_reel across cells)
  const heatMax = useMemo(() => {
    let m = 0;
    (data?.heatmap || []).forEach((row) => row.cells.forEach((c) => { if (c.bs_reel > m) m = c.bs_reel; }));
    return m || 1;
  }, [data]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Géomarketing"
        icon={Compass}
        subtitle="Meilleurs territoires, jours et canaux — pour orienter vos déploiements terrain"
      />

      <div className="flex justify-end">
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="grid w-full max-w-[760px] grid-cols-2 rounded-xl sm:grid-cols-3 lg:grid-cols-5" data-testid="geo-view-toggle">
            <TabsTrigger value="executive" className="rounded-lg" data-testid="geo-view-executive">Executive</TabsTrigger>
            <TabsTrigger value="analytique" className="rounded-lg" data-testid="geo-view-analytique">Analytique</TabsTrigger>
            <TabsTrigger value="spots" className="gap-1.5 rounded-lg" data-testid="geo-view-spots"><Store className="h-3.5 w-3.5" /> Meilleurs spots</TabsTrigger>
            <TabsTrigger value="pareto" className="gap-1.5 rounded-lg" data-testid="geo-view-pareto"><PieChart className="h-3.5 w-3.5" /> Pareto 20/80</TabsTrigger>
            <TabsTrigger value="booking" className="gap-1.5 rounded-lg" data-testid="geo-view-booking"><ClipboardCheck className="h-3.5 w-3.5" /> Plan de booking</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {view === 'spots' ? (
        <SpotsPanel body={body} />
      ) : view === 'pareto' ? (
        <SpotPareto body={body} />
      ) : view === 'booking' ? (
        <BookingPlan scopeAssociations={options?.associations || []} />
      ) : (
      <>
      {isForbidden ? (
        <EmptyState title="Accès restreint" body="Cette analyse est réservée aux administrateurs SHARITI." />
      ) : isError ? (
        <ErrorState title="Impossible de charger l'analyse" body="Le calcul a échoué ou expiré. Réessayez dans un instant." onRetry={refetch} />
      ) : isLoading ? (
        <><GridSkeleton count={3} /><CardSkeleton /></>
      ) : !data || data.record_count === 0 ? (
        <EmptyState title="Aucune donnée" body="Aucune donnée sur la période sélectionnée pour l'analyse géomarketing." />
      ) : (
        <Tabs value={view} className="space-y-6">
          {/* EXECUTIVE */}
          <TabsContent value="executive" className="mt-0 space-y-6" data-testid="geo-executive-panel">
            {/* Recommendation banner */}
            <div className="flex items-start gap-3 rounded-2xl border p-4 accent-bg-soft" data-testid="geo-reco">
              <Lightbulb className="mt-0.5 h-5 w-5 accent-text shrink-0" />
              <p className="text-sm text-foreground">
                Sur la période, concentrez vos équipes sur <span className="font-semibold">{ins.best_ville?.key}</span>,
                {' '}privilégiez le <span className="font-semibold">{ins.best_day?.key}</span> et le canal
                {' '}<span className="font-semibold">{ins.best_channel?.key}</span>.
                {ins.best_ville_transfo && (
                  <> Meilleure transformation : <span className="font-semibold">{ins.best_ville_transfo.key}</span> ({fmtPct(ins.best_ville_transfo.taux_transfo)}).</>
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <InsightCard icon={MapPin} label="Meilleur territoire" testid="insight-ville"
                value={ins.best_ville?.key} detail={ins.best_ville ? `${fmtInt(ins.best_ville.bs_reel)} BS validés · ${fmtPct(ins.best_ville.taux_transfo)}` : ''} />
              <InsightCard icon={CalendarDays} label="Meilleur jour" testid="insight-day"
                value={ins.best_day?.key} detail={ins.best_day ? `${fmtInt(ins.best_day.bs_reel)} BS validés · ${fmtNum(ins.best_day.productivite, 2)} BS/h` : ''} />
              <InsightCard icon={Radio} label="Meilleur canal" testid="insight-channel"
                value={ins.best_channel?.key} detail={ins.best_channel ? `${fmtInt(ins.best_channel.bs_reel)} BS validés · ${fmtPct(ins.best_channel.taux_transfo)}` : ''} />
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <SectionCard
                title="Top territoires"
                subtitle={GEO_METRICS[metric].label}
                className="xl:col-span-2"
                action={
                  <Select value={metric} onValueChange={setMetric}>
                    <SelectTrigger className="h-9 w-[190px] rounded-xl bg-card" data-testid="geo-metric-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(GEO_METRICS).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                }
                testid="geo-territory-chart">
                <HorizontalBars
                  data={territoryBars}
                  height={Math.max(220, territoryBars.length * 42)}
                  formatTip={(v) => GEO_METRICS[metric].fmt(v)}
                />
              </SectionCard>

              <SectionCard title="Meilleurs canaux" subtitle="Par BS validés" testid="geo-channels-card">
                <div className="space-y-3">
                  {channels.map((c) => (
                    <div key={c.key} className="rounded-xl border p-3" data-testid="geo-channel-item">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{c.key}</span>
                        <span className="tabular font-display font-semibold">{fmtInt(c.bs_reel)}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                        <span>Transfo {fmtPct(c.taux_transfo)}</span>
                        <span>·</span>
                        <span>{fmtNum(c.productivite, 2)} BS/h</span>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>

            <SectionCard title="Performance par jour de la semaine" subtitle={GEO_METRICS[metric].label} testid="geo-days-chart">
              <ComparisonBars
                data={dayBars}
                xKey="label"
                height={300}
                formatTip={(v) => GEO_METRICS[metric].fmt(v)}
                series={[{ key: 'value', name: GEO_METRICS[metric].label, color: 'var(--tenant-accent-600)' }]}
              />
            </SectionCard>
          </TabsContent>

          {/* ANALYTIQUE */}
          <TabsContent value="analytique" className="mt-0 space-y-6" data-testid="geo-analytique-panel">
            <SectionCard title="Territoires" subtitle="Classés par BS validés" bodyClassName="pt-0" testid="geo-territories-table-card">
              <GeoTable rows={territories} labelHead="Territoire / Région" testid="geo-territories-table" />
            </SectionCard>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <SectionCard title="Jours de la semaine" bodyClassName="pt-0" testid="geo-days-table-card">
                <GeoTable rows={days} labelHead="Jour" testid="geo-days-table" />
              </SectionCard>
              <SectionCard title="Canaux" bodyClassName="pt-0" testid="geo-channels-table-card">
                <GeoTable rows={channels} labelHead="Canal" testid="geo-channels-table" />
              </SectionCard>
            </div>

            {/* Heatmap jour x canal */}
            <SectionCard title="Carte de chaleur — Jour × Canal" subtitle="Intensité = BS validés" testid="geo-heatmap-card">
              <div className="overflow-x-auto">
                <table className="w-full border-separate border-spacing-1" data-testid="geo-heatmap">
                  <thead>
                    <tr>
                      <th className="p-2 text-left text-xs font-medium text-muted-foreground">Jour</th>
                      {(data.canaux || []).map((c) => (
                        <th key={c} className="p-2 text-center text-xs font-medium text-muted-foreground">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(data.heatmap || []).map((row) => (
                      <tr key={row.jour}>
                        <td className="p-2 text-sm font-medium">{row.jour}</td>
                        {row.cells.map((cell) => {
                          const intensity = Math.min(1, cell.bs_reel / heatMax);
                          return (
                            <td key={cell.canal} className="relative h-12 min-w-[90px] overflow-hidden rounded-lg border text-center align-middle">
                              <span className="absolute inset-0" style={{ background: 'var(--tenant-accent-500)', opacity: 0.12 + intensity * 0.78 }} />
                              <span className="relative z-10 tabular text-sm font-semibold" style={{ color: intensity > 0.5 ? 'var(--tenant-on-accent)' : 'inherit' }}>
                                {cell.bs_reel > 0 ? fmtInt(cell.bs_reel) : '—'}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </TabsContent>
        </Tabs>
      )}
      </>
      )}
    </div>
  );
}
